import * as React from "react";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import { useEffect } from "react";
import { Controller, Scene } from "react-scrollmagic";
import { Timeline, Tween } from "react-gsap";
import mangaInfo, { MangaInfo } from "../model/MangaInfo";
import styled from "styled-components";
import { main01_mobile, main01_pc, main02_mobile, main02_pc, mainImg } from "../model/DataConst";
import { OrientationContext } from "../provider/OrientationProvider";

const PanelDiv = styled.div`
	height: 100vh;
	width: 100vw;
	text-align: center;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export const MainVisual = () => {

	const imgParentRef = React.useRef<HTMLDivElement>( null );

	const context = React.useContext( OrientationContext );

	const [initShow, setInitShow] = React.useState( false );
	const [isVisible, setVisible] = React.useState( true );

	// 初回のみ実行
	React.useEffect( () => {
		let timer: any = null;
		const func = () => {
			if ( timer !== null ) {
				clearTimeout( timer );
			}
			timer = setTimeout( function () {
				// 画面の表示高さを110%地点を経過したら、隠しておく（iOSとかで無理にスクロールしようとした際とかに少し見えるから）
				setVisible( window.scrollY < window.innerHeight * 1.1 );
			}, 20 );
		};
		window.addEventListener( "scroll", func );

		return function cleanup() {
			window.removeEventListener( "scroll", func );
		};
	}, [] );

	useEffect( () => {
		mangaInfo.setWaitMainFunc( () => {
			setInitShow( true );
		} );
	}, [] );

	// 表示フラグ変更orサイズ変更時に再計算
	const imgStyle = React.useMemo( () => {
		const style: CSSProperties = {
			objectFit: "cover"
		};
		// 画像のスケール
		const scale = 3756 / 2400; // サイズは決め打ち

		// 表示領域のスケール
		const windowScale = window.innerWidth / window.innerHeight;

		// 画像のスケールの方が表示領域のスケールより小さければ幅を最大にする
		if ( scale < windowScale ) {
			style.width = "100%";
			return style;
		}

		// 逆なら高さを最大にする
		style.height = "100%";

		// 中心を表示するように移動
		if ( imgParentRef.current != null ) {
			imgParentRef.current.scrollTo( (3756 * (window.innerHeight / 2400) - window.innerWidth) * 0.5, 0 );
		}
		return style;
	}, [initShow, context.resize] );

	// 表示しない（画像ロード中）の場合はロード中であることを表す画面を表示
	// if ( !initShow ) {
	// 	return <div
	// 		style={{
	// 			height: "100%",
	// 			width: "100%",
	// 			background: "linear-gradient(0deg, rgb(223, 0, 58), #ffb03c)",
	// 			color: "white"
	// 		}}
	// 	>
	// 		Now Loading...
	// 	</div>;
	// }

	return (
		<section style={{
			width: "100vw",
			height: "100vh"
		}}>
			<Controller>
				{/*メインビジュアル*/}
				<Scene
					triggerHook={"onLeave"}
					pin>
					<PanelDiv style={{
						transition: "opacity .5s",
						opacity: isVisible ? 1 : 0
					}}>
						<Controller>
							<Scene
								triggerElement={"#root"}
							>
								{(
									progress: number
								) => (
									<Tween
										wrapper={
											<PanelDiv>
												<div ref={imgParentRef}
													 style={{
														 height: "100%"
													 }}>
												</div>
											</PanelDiv>}
										from={{
											opacity: 0
										}}
										to={{
											opacity: 1
										}}
										duration={5}
									>
										<img
											src={mainImg}
											alt={"Key Visual"}
											style={imgStyle}
										/>
									</Tween>
								)}
							</Scene>

							{/*文字列*/}
							<Scene
								triggerHook={"onLeave"}
							>
								<div style={{
									width: "100%",
									height: "100%",
									position: "absolute"
								}}>

									{/*前半*/}
									<Timeline
										duration={3}
										target={
											<div
												style={{
													top: "40%",
													position: "absolute",
												}}
											>
												<img
													src={main01_mobile}
													alt={"eComicシリーズ"}
													style={{
														height: "7vmin"
													}}/>
											</div>}
									>
										<Tween
											position={.5}
											duration={1}
											from={{
												css: {
													opacity: 0,
													left: "-100%",
													rotationX: -88,
													transformOrigin: 'center center'
												},
												ease: 'Strong.easeOut',
											}}
											to={{
												css: {
													opacity: 1,
													left: 0,
													rotationX: -88,
													transformOrigin: 'center center'
												},
												ease: 'Strong.easeOut',
											}}
										/>
										<Tween
											position={1}
											duration={2}
											from={{
												css: {
													rotationX: -88,
													transformOrigin: 'center center'
												},
												ease: 'slow(0.7, 0.7, false)',
											}}
											to={{
												css: {
													rotationX: 0,
													transformOrigin: 'center center'
												},
												ease: 'slow(0.7, 0.7, false)',
											}}
										/>
									</Timeline>

									{/*後半*/}
									<Timeline
										duration={4}
										target={<div
											style={{
												position: "absolute",
												top: "70%"
											}}
										>
											<img
												src={main02_mobile}
												alt={"電子書籍はもっとキレイになる"}
												style={{
													height: "7vmin"
												}}/>
										</div>}
									>
										<Tween
											position={1.5}
											duration={1}
											from={{
												css: {
													opacity: 0,
													right: "-100%",
													rotationX: -88,
													transformOrigin: 'center center'
												},
												ease: 'Strong.easeOut',
											}}
											to={{
												css: {
													opacity: 1,
													right: 0,
													rotationX: -88,
													transformOrigin: 'center center'
												},
												ease: 'Strong.easeOut',
											}}
										/>
										<Tween
											position={2}
											duration={2}
											from={{
												css: {
													rotationX: -88,
													transformOrigin: 'center center'
												},
												ease: 'slow(0.7, 0.7, false)',
											}}
											to={{
												css: {
													rotationX: 0,
													transformOrigin: 'center center'
												},
												ease: 'slow(0.7, 0.7, false)',
											}}
										/>
									</Timeline>
								</div>
							</Scene>
						</Controller>
					</PanelDiv>
				</Scene>
			</Controller>
		</section>
	);
};

export default MainVisual;